/**
 * RFQ Service (Solicitud de Cotización)
 *
 * CRUD for RFQs (Solicitudes de Cotización) and their line items, plus the
 * "mark sent" workflow.
 *
 * The RFQ lives as data in the DB (like a quotation/order record); the PDF is a
 * download-only convenience and is NOT persisted as a file. The document that
 * IS stored as a file is the purchase invoice (factura de compra), handled
 * separately by the invoices/purchases module — out of scope here.
 *
 * RFQs are price-less quote requests handed to hardware stores (ferreterías).
 * `rfq_number`, timestamps and the numeric sequence are server-knowable and are
 * assigned by the DB trigger — never by the client/model. Captura textual:
 * `description` is stored verbatim (jerga/marcas are not corrected).
 *
 * @module services/rfqService
 */

import { supabase } from './supabase';
import { terminoParaFiltro, columnaNormalizada } from '../utils/normalizacionBusqueda';

export const RFQ_STATUS = {
  DRAFT: 'draft',
  SENT: 'sent',
  ARCHIVED: 'archived',
};

export const RFQ_STATUS_LABELS = {
  draft: 'Borrador',
  sent: 'Enviada',
  archived: 'Archivada',
};

export const RFQ_STATUS_VARIANTS = {
  draft: 'secondary',
  sent: 'success',
  archived: 'dark',
};

/**
 * Normalize a raw item into an rfq_items row.
 * If a quantity is not provided, the line is flagged `is_inferred` so the
 * silence bug (#356) never happens — the line is kept, just marked.
 */
const buildItemRow = (rfqId, item, index) => ({
  rfq_id: rfqId,
  alcance: item.alcance ?? null,
  sort_order: item.sort_order ?? index,
  name: item.name ?? null,
  description: item.description,
  quantity: item.quantity ?? null,
  unit: item.unit ?? null,
  is_inferred: item.is_inferred ?? (item.quantity == null),
  note: item.note ?? null,
  inventory_id: item.inventory_id ?? null,
});

/**
 * List RFQs with optional filters.
 * @param {Object} filters - { projectId?, status?, search?, dateFrom?, dateTo? }
 */
export const listRfqs = async (filters = {}) => {
  try {
    let query = supabase
      .from('rfqs')
      .select(
        `
        *,
        project:projects(id, project_number, title, client:clients!projects_client_id_fkey(id, company_name)),
        client:clients!rfqs_client_id_fkey(id, company_name),
        items:rfq_items(count)
      `
      );

    if (filters.projectId) {
      query = query.eq('project_id', filters.projectId);
    }
    if (filters.status) {
      query = query.eq('status', filters.status);
    }
    if (filters.dateFrom) {
      query = query.gte('created_at', filters.dateFrom);
    }
    if (filters.dateTo) {
      query = query.lte('created_at', filters.dateTo);
    }
    if (filters.search) {
      // #742/#739 — `title` contra su columna generada; `rfq_number` crudo,
      // que es un código. Mismo término normalizado para los dos.
      const termino = terminoParaFiltro(filters.search);
      query = query.or(
        `rfq_number.ilike.%${termino}%,${columnaNormalizada('title')}.ilike.%${termino}%`
      );
    }

    query = query.order('created_at', { ascending: false });

    const { data, error } = await query;
    if (error) throw error;

    return { data, error: null };
  } catch (error) {
    console.error('listRfqs error:', error);
    return { data: null, error };
  }
};

/**
 * Get one RFQ with its items (ordered by sort_order) and project → client.
 */
export const getRfq = async (id) => {
  try {
    const { data: rfq, error } = await supabase
      .from('rfqs')
      .select(
        `
        *,
        project:projects(id, project_number, title, client_id, client:clients!projects_client_id_fkey(id, company_name, legal_id, primary_contact_email, primary_contact_phone, address_line1)),
        client:clients!rfqs_client_id_fkey(id, company_name, legal_id, primary_contact_email, primary_contact_phone, address_line1)
      `
      )
      .eq('id', id)
      .single();

    if (error) throw error;

    const { data: items, error: itemsError } = await supabase
      .from('rfq_items')
      .select('*')
      .eq('rfq_id', id)
      .order('sort_order', { ascending: true });

    if (itemsError) throw itemsError;

    rfq.items = items || [];
    return { data: rfq, error: null };
  } catch (error) {
    console.error('getRfq error:', error);
    return { data: null, error };
  }
};

/**
 * Create an RFQ header plus optional line items.
 * `rfq_number` is assigned server-side by the DB trigger.
 * `project_id` is optional (#422 — "compra por excepción" with no project).
 * `client_id` is only meaningful when there is no project; with a project,
 * the client is the project's own client (project_id -> projects.client_id).
 * @param {Object} args - { project_id?, client_id?, title?, notes?, items? }
 */
export const createRfq = async ({
  project_id = null,
  client_id = null,
  title = null,
  notes = null,
  items = [],
}) => {
  try {
    const { data: { user }, error: userError } = await supabase.auth.getUser();
    if (userError) throw userError;

    const { data: rfq, error: rfqError } = await supabase
      .from('rfqs')
      .insert({ project_id, client_id, title, notes, created_by: user.id })
      .select()
      .single();

    if (rfqError) throw rfqError;

    if (items.length) {
      const rows = items.map((it, i) => buildItemRow(rfq.id, it, i));
      const { error: itemsError } = await supabase.from('rfq_items').insert(rows);
      if (itemsError) throw itemsError;
    }

    return { data: rfq, error: null };
  } catch (error) {
    console.error('createRfq error:', error);
    return { data: null, error };
  }
};

/**
 * Update an RFQ header. Items are managed via replaceRfqItems.
 */
export const updateRfq = async (id, patch = {}) => {
  try {
    const allowed = ['project_id', 'client_id', 'title', 'notes', 'status', 'sent_at', 'snapshot_pdf_path'];
    const payload = Object.fromEntries(
      Object.entries(patch).filter(([k]) => allowed.includes(k))
    );

    const { data, error } = await supabase
      .from('rfqs')
      .update(payload)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('updateRfq error:', error);
    return { data: null, error };
  }
};

/**
 * Replace all line items for an RFQ (delete then insert).
 */
export const replaceRfqItems = async (rfqId, items = []) => {
  try {
    const { error: delError } = await supabase
      .from('rfq_items')
      .delete()
      .eq('rfq_id', rfqId);

    if (delError) throw delError;

    if (items.length) {
      const rows = items.map((it, i) => buildItemRow(rfqId, it, i));
      const { data, error } = await supabase
        .from('rfq_items')
        .insert(rows)
        .select();

      if (error) throw error;
      return { data, error: null };
    }

    return { data: [], error: null };
  } catch (error) {
    console.error('replaceRfqItems error:', error);
    return { data: null, error };
  }
};

/**
 * Mark an RFQ as sent: status='sent', sent_at=now.
 * The RFQ lives as data in the DB; the PDF is download-only and is not
 * persisted. (The file that gets stored is the purchase invoice, handled
 * elsewhere.)
 */
export const markRfqSent = async (id) => {
  try {
    // #655 — Sin líneas no hay pedido.
    //
    // Gemela de `assertRfqHasItems` en el carril MCP
    // (mcp-server/src/tools/rfqTools.js). Acá se pasaba directo al
    // `update status = 'sent'`: nada contaba las líneas. Marcar una RFQ vacía
    // como enviada afirma que la ferretería recibió un pedido y que hay
    // precios en camino — el que la lee después espera una respuesta que nunca
    // va a llegar, porque nunca se pidió nada.
    //
    // Falla CERRADA: si la cuenta no se puede leer, no se marca. El costo de
    // equivocarse para este lado es un reintento; para el otro, un eslabón del
    // abastecimiento que dice "ya pedí" sin haber pedido.
    const { count: cantidadItems, error: itemsError } = await supabase
      .from('rfq_items')
      .select('id', { count: 'exact', head: true })
      .eq('rfq_id', id);

    if (itemsError) throw itemsError;

    if (!cantidadItems) {
      throw new Error(
        'No se puede marcar como enviada una solicitud sin líneas. Agregá al ' +
        'menos un ítem a la solicitud y volvé a marcarla. Si todavía no sabés ' +
        'qué pedirle a la ferretería, dejala en borrador.'
      );
    }

    const { data, error } = await supabase
      .from('rfqs')
      .update({ status: RFQ_STATUS.SENT, sent_at: new Date().toISOString() })
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;
    return { data, error: null };
  } catch (error) {
    console.error('markRfqSent error:', error);
    return { data: null, error };
  }
};

/**
 * Delete an RFQ. rfq_items cascade via the FK.
 */
export const deleteRfq = async (id) => {
  try {
    const { error } = await supabase.from('rfqs').delete().eq('id', id);
    if (error) throw error;
    return { error: null };
  } catch (error) {
    console.error('deleteRfq error:', error);
    return { error };
  }
};

const rfqService = {
  RFQ_STATUS,
  RFQ_STATUS_LABELS,
  RFQ_STATUS_VARIANTS,
  listRfqs,
  getRfq,
  createRfq,
  updateRfq,
  replaceRfqItems,
  markRfqSent,
  deleteRfq,
};

export default rfqService;
